import React from 'react';
import { Box, Chip, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { AddCircleOutline, ArrowRightAlt, EditOutlined, RemoveCircleOutline, SwapVert } from '@material-ui/icons';
import { taskFieldTypeLabel } from '../../../../../common/task-field-type-labels';

export interface SyncChange {
  diffType: string;
  entityType: string;
  entityId: string;
  fieldPath: string | null;
  fieldLabel: string;
  entityLabel: string;
  itemName: string;
  itemFieldType: string;
  previousValue: string;
  newValue: string;
  summary: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '8px 0',
      borderBottom: `1px solid ${theme.color.grey.border}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    icon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
      marginRight: '10px',
      marginTop: '2px',
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.color.grey.neutralBrand900,
    },
    itemName: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
      paddingLeft: '6px',
    },
    values: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      paddingTop: '2px',
    },
    previous: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
      textDecoration: 'line-through',
    },
    next: {
      fontSize: '13px',
      color: theme.color.grey.neutralBrand900,
      fontWeight: 500,
    },
    arrow: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
      margin: '0 8px',
    },
    empty: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
      fontStyle: 'italic',
    },
    typeChip: {
      marginLeft: '8px',
      height: '18px',
      fontSize: '11px',
      color: theme.color.grey.neutral300,
      borderColor: theme.color.grey.border,
    },
  })
);

const changeIcon = (diffType: string) => {
  if (diffType === 'entity_added') return AddCircleOutline;
  if (diffType === 'entity_removed') return RemoveCircleOutline;
  if (diffType === 'reordered') return SwapVert;
  return EditOutlined;
};

const ChangeValues = ({ change }: { change: SyncChange }) => {
  const classes = useStyles();
  const hasPrevious = change.previousValue !== '';
  const hasNext = change.newValue !== '';

  if (!hasPrevious && !hasNext) {
    return null;
  }

  return (
    <Box className={classes.values}>
      {hasPrevious ? (
        <Typography className={classes.previous}>{change.previousValue}</Typography>
      ) : (
        <Typography className={classes.empty}>Empty</Typography>
      )}
      <ArrowRightAlt className={classes.arrow} />
      {hasNext ? (
        <Typography className={classes.next}>{change.newValue}</Typography>
      ) : (
        <Typography className={classes.empty}>Empty</Typography>
      )}
    </Box>
  );
};

export const SyncChangeList = ({ changes }: { changes: SyncChange[] }) => {
  const classes = useStyles();

  return (
    <Box>
      {changes.map((change) => {
        const Icon = changeIcon(change.diffType);
        const fieldTypeLabel = taskFieldTypeLabel(change.itemFieldType);

        return (
          <Box className={classes.row} key={`${change.diffType}-${change.entityId}-${change.fieldPath ?? ''}`}>
            <Icon className={classes.icon} />
            <Box>
              <Box display='flex' alignItems='center' flexWrap='wrap'>
                <Typography className={classes.label}>{change.summary}</Typography>
                {change.itemName !== '' && <Typography className={classes.itemName}>{change.itemName}</Typography>}
                {fieldTypeLabel !== '' && (
                  <Chip size='small' variant='outlined' label={fieldTypeLabel} className={classes.typeChip} />
                )}
              </Box>
              <ChangeValues change={change} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
